import React, { useState, useEffect, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { Chart } from 'primereact/chart';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Message } from 'primereact/message';
import { Tag } from 'primereact/tag';
import { Calendar } from 'primereact/calendar';
import { Menu } from 'primereact/menu';
import { Toolbar } from 'primereact/toolbar';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { ProgressBar } from 'primereact/progressbar';
import { Toast } from 'primereact/toast';
import { OverlayPanel } from 'primereact/overlaypanel';
import { LayoutContext } from '../layout/context/layoutcontext';
import axiosInstance from '../config/axios.config';
import { menuService } from '../services/menuService';

// Professional input styling
const inputStyles = `
    .professional-search {
        position: relative;
    }
    
    .professional-search .p-inputtext {
        border-radius: 8px;
        border: 1px solid #d1d5db;
        padding: 0.75rem 1rem 0.75rem 2.5rem;
        transition: all 0.2s ease;
        background: #ffffff;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    }
    
    .professional-search .p-inputtext:focus {
        border-color: #667eea;
        box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1), 0 1px 2px rgba(0, 0, 0, 0.05);
        outline: none;
    }
    
    .professional-search .p-inputtext::placeholder {
        color: #6b7280;
        font-style: italic;
    }
    
    .professional-search .pi-search {
        position: absolute;
        left: 0.75rem;
        top: 50%;
        transform: translateY(-50%);
        color: #667eea;
        z-index: 1;
        font-size: 0.9rem;
    }
`;

// ─── Quick-action cards ──────────────────────────────────────────────────────
// Hover choreography: the whole card lifts and deepens its shadow, an accent
// bar wipes across the top, the icon badge pops with a slight overshoot, and
// the CTA arrow slides right. Each card passes its own --action-accent so the
// bar and border pick up that card's colour.
const actionCardStyles = `
    .action-card {
        position: relative;
        overflow: hidden;
        border: 1px solid var(--surface-border);
        box-shadow: 0 1px 2px rgba(10, 19, 32, 0.04);
        transition: transform .28s cubic-bezier(.4,0,.2,1),
                    box-shadow .28s cubic-bezier(.4,0,.2,1),
                    border-color .28s ease;
    }
    .action-card::before {
        content: '';
        position: absolute;
        top: 0; left: 0; right: 0;
        height: 3px;
        background: var(--action-accent);
        transform: scaleX(0);
        transform-origin: left;
        transition: transform .3s cubic-bezier(.4,0,.2,1);
    }
    .action-card:hover,
    .action-card:focus-visible {
        transform: translateY(-6px);
        box-shadow: 0 12px 28px rgba(10, 19, 32, 0.12), 0 4px 8px rgba(10, 19, 32, 0.06);
        border-color: var(--action-accent);
        outline: none;
    }
    .action-card:hover::before,
    .action-card:focus-visible::before {
        transform: scaleX(1);
    }
    .action-card-badge {
        /* overshoot easing gives the badge a springy pop rather than a linear grow */
        transition: transform .28s cubic-bezier(.34,1.56,.64,1);
    }
    .action-card:hover .action-card-badge,
    .action-card:focus-visible .action-card-badge {
        transform: scale(1.12) rotate(-6deg);
    }
    .action-card-cta i {
        display: inline-block;
        transition: transform .28s cubic-bezier(.4,0,.2,1);
    }
    .action-card:hover .action-card-cta i,
    .action-card:focus-visible .action-card-cta i {
        transform: translateX(5px);
    }
    @media (prefers-reduced-motion: reduce) {
        .action-card,
        .action-card::before,
        .action-card-badge,
        .action-card-cta i {
            transition: none;
        }
        .action-card:hover,
        .action-card:hover .action-card-badge,
        .action-card:hover .action-card-cta i {
            transform: none;
        }
    }
`;

/**
 * One dashboard shortcut. Replaces four near-identical 20-line blocks.
 *
 * `color` is a PrimeReact palette name (blue/green/purple/orange) and drives
 * the badge tint, title, CTA and hover accent together, so each card reads as
 * one colour rather than a mix.
 */
const ActionCard = ({ icon, color, title, description, cta, onClick, delayClass }) => (
    <div className={`col-12 md:col-6 lg:col-3 fadein animation-duration-500 ${delayClass}`}>
        <div
            className="action-card surface-card border-round-xl p-4 h-full mb-4 cursor-pointer flex flex-column text-center"
            style={{ '--action-accent': `var(--${color}-500)` }}
            role="button"
            tabIndex={0}
            onClick={onClick}
            onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onClick();
                }
            }}
        >
            <div className="flex justify-content-center mb-3">
                <div className={`action-card-badge border-circle w-4rem h-4rem bg-${color}-100 flex align-items-center justify-content-center`}>
                    <i className={`pi ${icon} text-2xl text-${color}-600`} aria-hidden="true" />
                </div>
            </div>
            <h5 className={`font-bold mb-2 text-${color}-600`}>{title}</h5>
            <p className="text-600 text-sm line-height-3 mb-4 flex-1">{description}</p>
            <span className={`action-card-cta font-semibold text-sm text-${color}-600`}>
                {cta} <i className="pi pi-arrow-right text-xs" aria-hidden="true" />
            </span>
        </div>
    </div>
);

const UnifiedDashboard = () => {
    const navigate = useNavigate();
    const { layoutConfig, setSelectedLoanUrl } = useContext(LayoutContext);
  
    const toast = useRef(null);
    

    // State management
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [dashboardData, setDashboardData] = useState(null);
    const [selectedPeriod, setSelectedPeriod] = useState('month');
    const [customDateRange, setCustomDateRange] = useState(null);
    const [refreshing, setRefreshing] = useState(false);

    // Profile completion state
    const [profileCompletion, setProfileCompletion] = useState(null);
    const [showProfileAlert, setShowProfileAlert] = useState(false);

    // Recent Reports table state
    const [reportsFirst, setReportsFirst] = useState(0);
    const [reportsRows, setReportsRows] = useState(10);
    const [globalFilterValue, setGlobalFilterValue] = useState('');

    // Credit Transactions table state
    const [transactionsFirst, setTransactionsFirst] = useState(0);
    const [transactionsRows, setTransactionsRows] = useState(10);

    // Period options for dropdown
    const periodOptions = [
        { label: 'Today', value: 'today' },
        { label: 'Yesterday', value: 'yesterday' },
        { label: 'Last 7 Days', value: 'week' },
        { label: 'Last 30 Days', value: 'month' }, 
        { label: 'Last 3 Months', value: 'quarter' },
        { label: 'Last Year', value: 'year' },
        { label: 'All Time', value: 'all' },
        { label: 'Custom Range', value: 'custom' }
    ];

    // Chart configurations
    const [reportTypesChart, setReportTypesChart] = useState({});
    const [reportTypesChartOptions, setReportTypesChartOptions] = useState({});
    const [creditTrendsChart, setCreditTrendsChart] = useState({});
    const [creditTrendsChartOptions, setCreditTrendsChartOptions] = useState({});

    // Fetch dashboard data
    const fetchDashboardData = async (period = selectedPeriod, dates = null) => {
        try {
            setError(null);
            const params = { period };

            if (dates && dates.length === 2) {
                params.start_date = dates[0].toISOString().split('T')[0];
                params.end_date = dates[1].toISOString().split('T')[0];
            }

            console.log('Fetching dashboard data with params:', params);
            const response = await axiosInstance.get('/dashboard', { params });

            console.log('UnifiedDashboard API Response:', response);

            if (response.data) {
                // Handle successful response even if data structure is different
                setDashboardData(response.data);
                
                // Only initialize charts if we have the expected data structure
                if (response.data.data) {
                    initializeCharts(response.data.data);

                    // Handle profile completion
                    const data = response.data.data;
                    if (data.profile_completion) {
                        console.log('✅ Profile completion found in UnifiedDashboard:', data.profile_completion);
                        setProfileCompletion(data.profile_completion);
                        setShowProfileAlert(!data.profile_completion.completed);
                    } else {
                        console.log('❌ No profile completion data in UnifiedDashboard');
                        // Force show for incomplete profiles
                        setProfileCompletion({
                            completed: false,
                            percentage: 0,
                            next_step: 1,
                            message: 'Please complete your profile to access all features'
                        });
                        setShowProfileAlert(true);
                    }

                    // Handle notifications
                    if (data.notifications && data.notifications.length > 0) {
                        const profileNotification = data.notifications.find(n => n.action === 'complete_profile');
                        if (profileNotification) {
                            setShowProfileAlert(true);
                        }
                    }
                } else {
                    console.warn('Dashboard API returned unexpected data structure:', response.data);
                    // Set minimal default data to prevent crashes
                    setDashboardData({
                        data: {
                            wallet_balance: 0,
                            reports_summary: {},
                            recent_reports: [],
                            credit_transactions: { transactions: [] }
                        },
                        date_range: {
                            period_label: 'Current Period',
                            start: new Date().toLocaleDateString(),
                            end: new Date().toLocaleDateString()
                        }
                    });
                }
            } else {
                throw new Error('No data received from dashboard API');
            }
        } catch (err) {
            console.error('Dashboard fetch error:', err);
            setError(err.response?.data?.error || err.message || 'Failed to load dashboard data');
            
            // Set minimal fallback data to prevent crashes
            setDashboardData({
                data: {
                    wallet_balance: 0,
                    reports_summary: {},
                    recent_reports: [],
                    credit_transactions: { transactions: [] }
                },
                date_range: {
                    period_label: 'Current Period',
                    start: new Date().toLocaleDateString(),
                    end: new Date().toLocaleDateString()
                }
            });
        } finally {
            // Ensure loading is always set to false
            setLoading(false);
            setRefreshing(false);
            console.log('Dashboard loading completed');
        }
    };

    // Initialize charts with API data
    const initializeCharts = (data) => {
        const documentStyle = getComputedStyle(document.documentElement);
        const textColor = documentStyle.getPropertyValue('--text-color') || '#495057';
        const textColorSecondary = documentStyle.getPropertyValue('--text-color-secondary') || '#6c757d';
        const surfaceBorder = documentStyle.getPropertyValue('--surface-border') || '#dee2e6';

        // Report Types Distribution Chart
        if (data.report_types_distribution && data.report_types_distribution.length > 0) {
            const labels = data.report_types_distribution.map(item => item.name);
            const values = data.report_types_distribution.map(item => item.count);
            const colors = ['#42A5F5', '#66BB6A', '#FFA726', '#26C6DA', '#7E57C2', '#FF7043'];

            setReportTypesChart({
                labels: labels,
                datasets: [{
                    data: values,
                    backgroundColor: colors.slice(0, labels.length),
                    hoverBackgroundColor: colors.slice(0, labels.length).map(c => c + 'DD'),
                    borderWidth: 0
                }]
            });

            setReportTypesChartOptions({
                plugins: {
                    legend: {
                        labels: {
                            color: textColor,
                            usePointStyle: true
                        },
                        position: 'bottom'
                    }
                },
                maintainAspectRatio: false,
                responsive: true
            });
        }

        // Credit Trends Chart
        if (data.credit_transactions && data.credit_transactions.summary) {
            const summary = data.credit_transactions.summary;

            setCreditTrendsChart({
                labels: ['Credits Added', 'Credits Used'],
                datasets: [{
                    label: 'Credits',
                    data: [summary.total_credited, summary.total_debited],
                    backgroundColor: ['#4CAF50', '#F44336'],
                    borderColor: ['#4CAF50', '#F44336'],
                    borderWidth: 1
                }]
            });

            setCreditTrendsChartOptions({
                indexAxis: 'y',
                plugins: {
                    legend: {
                        display: false
                    }
                },
                scales: {
                    x: {
                        ticks: { color: textColorSecondary },
                        grid: { color: surfaceBorder }
                    },
                    y: {
                        ticks: { color: textColorSecondary },
                        grid: { color: surfaceBorder }
                    }
                },
                maintainAspectRatio: false,
                responsive: true
            });
        }
    };

    // Load initial data with timeout protection
    useEffect(() => {
        console.log('UnifiedDashboard mounting, starting data fetch...');
        
        // Set up a timeout to prevent infinite loading
        const loadingTimeout = setTimeout(() => {
            console.warn('Dashboard loading timeout - forcing completion');
            setLoading(false);
            setRefreshing(false);
            if (!dashboardData) {
                setError('Dashboard loading timed out. Please refresh the page.');
            }
        }, 10000); // 10 second timeout
        
        fetchDashboardData().then(() => {
            clearTimeout(loadingTimeout);
        }).catch(() => {
            clearTimeout(loadingTimeout);
        });
        
        return () => {
            clearTimeout(loadingTimeout);
        };
    }, []);

    // Handle period change
    const handlePeriodChange = (e) => {
        const newPeriod = e.value;
        setSelectedPeriod(newPeriod);

        if (newPeriod !== 'custom') {
            setCustomDateRange(null);
            setRefreshing(true);
            fetchDashboardData(newPeriod);
        }
    };

    // Handle custom date range
    const handleDateRangeChange = (e) => {
        setCustomDateRange(e.value);
        if (e.value && e.value.length === 2 && e.value[0] && e.value[1]) {
            setRefreshing(true);
            fetchDashboardData('custom', e.value);
        }
    };

    // Handle refresh
    const handleRefresh = () => {
        setRefreshing(true);
        fetchDashboardData();
    };

    // Helper function to get Equifax service ID dynamically
    const getEquifaxServiceId = async () => {
        try {
            const categorizedServices = await menuService.fetchReportServices();
            
            // Search through all categories and services for Equifax
            if (categorizedServices && typeof categorizedServices === 'object') {
                const categoriesArray = Object.values(categorizedServices);
                
                for (const category of categoriesArray) {
                    if (category.services) {
                        const equifaxService = category.services.find(service => 
                            service.name.toLowerCase().includes('equifax')
                        );
                        if (equifaxService) {
                            return equifaxService.id;
                        }
                    }
                }
            }
            
            // Fallback to service ID 1 if not found
            return 1;
        } catch (error) {
            console.error('Error getting Equifax service ID:', error);
            return 1; // Fallback service ID
        }
    };

    // Column Templates for Recent Reports Table
    const reportNameTemplate = (rowData) => {
        // Display the exact report name from database
        return <span className="font-semibold">{rowData.report_name}</span>;
    };

    const fetchedDateTemplate = (rowData) => {
        // Parse date in d-m-Y H:i:s format from backend
        if (!rowData.fetched_date) return 'N/A';

        // Split date and time
        const [datePart, timePart] = rowData.fetched_date.split(' ');
        if (!datePart) return 'Invalid Date';

        // Parse date components
        const [day, month, year] = datePart.split('-');

        // Create proper date string that JavaScript can parse
        const dateStr = `${year}-${month}-${day} ${timePart || '00:00:00'}`;
        const date = new Date(dateStr);

        // Check if date is valid
        if (isNaN(date.getTime())) {
            return rowData.fetched_date; // Return original if parsing fails
        }

        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const attachmentStatusTemplate = (rowData) => {
        const isAssigned = rowData.attachment_status === 'assigned';
        return (
            <Tag
                value={isAssigned ? 'Assigned' : 'Unassigned'}
                severity={isAssigned ? 'success' : 'warning'}
                icon={`pi pi-${isAssigned ? 'check' : 'exclamation-triangle'}`}
            />
        );
    };

    const actionTemplate = (rowData) => {
        return (
            <div className="flex align-items-center gap-1">
                <Button
                    icon="pi pi-eye"
                    className="p-button-rounded p-button-text p-button-sm"
                    severity="info"
                    onClick={() => navigate(`/app/report-history`)}
                    tooltip="View Report"
                    tooltipOptions={{ position: 'top' }}
                    aria-label="View Report"
                />
                <Button
                    icon="pi pi-download"
                    className="p-button-rounded p-button-text p-button-sm"
                    severity="success"
                    onClick={() => {
                        // Handle download logic
                        console.log('Download report:', rowData.report_id);
                    }}
                    tooltip="Download Report"
                    tooltipOptions={{ position: 'top' }}
                    aria-label="Download Report"
                />
            </div>
        );
    };

    // Status badge template (keeping for other uses)
    const statusBodyTemplate = (rowData) => {
        const getSeverity = (status) => {
            switch (status?.toLowerCase()) {
                case 'success':
                case 'fetched':
                case 'completed':
                    return 'success';
                case 'failed':
                case 'error':
                    return 'danger';
                case 'pending':
                case 'processing':
                    return 'warning';
                default:
                    return 'info';
            }
        };
        return <Tag value={rowData.status || 'Unknown'} severity={getSeverity(rowData.status)} />;
    };

    // Transaction type template
    const transactionTypeTemplate = (rowData) => {
        // Handle both string types ('credit'/'debit') and numeric types (1/2)
        const typeText = rowData.type === 'credit' || rowData.type === 1 ? 'Credit' : 'Debit';
        const isCredit = rowData.type === 'credit' || rowData.type === 1;
        const severity = isCredit ? 'success' : 'danger';
        const icon = isCredit ? 'pi-plus' : 'pi-minus';
        
        return (
            <div className="flex align-items-center gap-2">
                <i className={`pi ${icon} text-${severity === 'success' ? 'green' : 'red'}-500`}></i>
                <span className={`text-${severity === 'success' ? 'green' : 'red'}-500 font-semibold`}>
                    {typeText}
                </span>
            </div>
        );
    };

    // Transaction date template
    const transactionDateTemplate = (rowData) => {
        if (!rowData.created_at) return 'N/A';

        try {
            const date = new Date(rowData.created_at);
            
            // Check if date is valid
            if (isNaN(date.getTime())) {
                return rowData.created_at; // Return original if parsing fails
            }

            // Format as dd/mm/yyyy hh:mm AM/PM
            return date.toLocaleString('en-GB', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: true
            });
        } catch (error) {
            return rowData.created_at; // Return original if error
        }
    };

    // Toolbar templates for Recent Reports table
    const leftToolbarTemplate = () => {
        return (

            <div className="flex gap-2">
                {/* <div className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilterValue}
                        onChange={(e) => setGlobalFilterValue(e.target.value)}
                        placeholder="Search reports..."
                        className="w-full"
                    />
                </div> */}

                <div className="col-12 mb-2 lg:col-4 lg:mb-0">
                    <IconField iconPosition="left">
                        <InputIcon className="pi pi-search" />
                        <InputText 
                            value={globalFilterValue}
                            onChange={(e) => setGlobalFilterValue(e.target.value)}
                            placeholder="Search reports..." 
                        />
                    </IconField>
                </div>
            </div>
        );
    };

    const rightToolbarTemplate = () => {
        return (
            <div className="flex gap-2">
                {/* <Button 
                    icon="pi pi-refresh" 
                    className="p-button-outlined" 
                    onClick={() => fetchDashboardData()}
                    tooltip="Refresh"
                /> */}
                <Button
                    label="View All"
                    icon="pi pi-external-link"
                    className="p-button-sm"
                    onClick={() => navigate('/app/report-history')}
                />
            </div>
        );
    };

    // Quick action menu items
    // const quickActionItems = [
    //     {
    //         label: 'Generate Report',
    //         icon: 'pi pi-file',
    //         items: [
    //             { label: 'Equifax Report', icon: 'pi pi-chart-line', command: () => navigate('/app/equifax-report') },
    //             { label: 'Bank Statement', icon: 'pi pi-wallet', command: () => navigate('/app/bank-statement') },
    //             { label: 'PAN Verification', icon: 'pi pi-id-card', command: () => navigate('/app/pan-verification') }
    //         ]
    //     },
    //     {
    //         label: 'View History',
    //         icon: 'pi pi-history',
    //         command: () => navigate('/app/report-history')
    //     },
    //     {
    //         label: 'Add Credits',
    //         icon: 'pi pi-plus-circle',
    //         command: () => navigate('/app/wallet')
    //     }
    // ];

    // Loading state
    if (loading && !refreshing) {
        return (
            <div className="flex align-items-center justify-content-center min-h-screen">
                <ProgressSpinner />
            </div>
        );
    }

    // Error state
    if (error && !dashboardData) {
        return (
            <div className="grid">
                <div className="col-12">
                    <Card>
                        <Message severity="error" text={error} />
                        <div className="mt-3">
                            <Button label="Retry" icon="pi pi-refresh" onClick={() => fetchDashboardData()} />
                        </div>
                    </Card>
                </div>
            </div>
        );
    }

    // Get data from API response
    const data = dashboardData?.data || {};
    const dateRange = dashboardData?.date_range || {};
    const walletBalance = data.wallet_balance || 0;
    const reportsSummary = data.reports_summary || {};
    const recentReports = data.recent_reports || [];
    const creditTransactions = data.credit_transactions || {};
    const periodComparison = data.period_comparison || {};

    return (
        <>
            <Toast ref={toast} />

            {/* Profile Completion Section */}
            {showProfileAlert && profileCompletion && (
                <div className="grid mb-4">
                    <div className="col-12">
                        <div className="surface-card p-4 border-round bg-orange-500 shadow-2 mb-4">
                            <div className="flex align-items-center justify-content-between">
                                <div className="flex align-items-center">
                                    <div className="border-circle w-3rem h-3rem bg-blue-100 flex align-items-center justify-content-center mr-3">
                                        <i className="pi pi-user text-blue-600 text-xl"></i>
                                    </div>
                                    <div>
                                        <div className="text-900 font-medium text-xl mb-2">Complete Your Profile</div>
                                        <div className="text-600 line-height-3 mb-3">
                                            Complete your profile to unlock all verification services and features. This helps us provide better service recommendations.
                                        </div>
                                        <div className="flex align-items-center gap-3">
                                            <div className="flex-1">
                                                <div className="bg-gray-200 border-round" style={{ height: '8px' }}>
                                                    <div 
                                                        className="bg-orange-500 border-round h-full" 
                                                        style={{ width: `${profileCompletion.percentage || 0}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                            <span className="text-orange-500 font-medium">{profileCompletion.percentage || 0}% Complete</span>
                                        </div>
                                        <div className="text-500 text-sm mt-1">
                                            {profileCompletion.missing_fields ? 
                                                `Missing: ${profileCompletion.missing_fields.join(', ')}` : 
                                                'All required fields completed'
                                            }
                                        </div>
                                    </div>
                                </div>
                                <div>
                                    <Button 
                                        label="Complete Now" 
                                        icon="pi pi-arrow-right" 
                                        iconPos="right"
                                        className="p-button-rounded"
                                        onClick={() => navigate('/app/profile/complete')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid">
                {/* Header Section */}
                <div className="col-12">
                    <div className="flex flex-column sm:flex-row justify-content-between align-items-start sm:align-items-center mb-4">
                        <div>
                            <h1 className="text-xl font-semibold m-0">Dashboard Overview</h1>
                            <p className="text-500 mt-1">
                                {/* {dateRange.period_label} ({dateRange.start} to {dateRange.end}) */}
                            </p>
                        </div>
                        <div className="flex gap-2 mt-3 sm:mt-0">
                            <Button
                                label="Credit Wallet"
                                icon="pi pi-wallet"
                                onClick={() => navigate('/app/credit-wallet')}
                                className="p-button-primary"
                                // badge={walletBalance ? `₹${walletBalance.toLocaleString()}` : null}
                                badgeClassName="p-badge-info"
                            />
                            <Dropdown
                                value={selectedPeriod}
                                options={periodOptions}
                                onChange={handlePeriodChange}
                                className="w-12rem"
                                placeholder="Select Period"
                            />
                            {selectedPeriod === 'custom' && (
                                <Calendar
                                    value={customDateRange}
                                    onChange={handleDateRangeChange}
                                    selectionMode="range"
                                    dateFormat="yy-mm-dd"
                                    placeholder="Select Date Range"
                                    className="w-15rem"
                                />
                            )}
                            {/* <Button
                                icon={refreshing ? "pi pi-spin pi-spinner" : "pi pi-refresh"}
                                onClick={handleRefresh}
                                disabled={refreshing}
                                className="p-button-outlined"
                                tooltip="Refresh Data"
                            />
                            <Button
                                icon="pi pi-bars"
                                className="p-button-text"
                                onClick={(e) => menuRef.current.toggle(e)}
                            />
                            <Menu model={quickActionItems} popup ref={menuRef} /> */}
                        </div>
                    </div>
                </div>

                {/* Main Action Cards */}
                {/* <div className="col-12 md:col-6">
                <Card 
                    className="mb-3 cursor-pointer transition-colors transition-duration-200 hover:surface-hover"
                    onClick={() => navigate('/app/credit-wallet')}
                >
                    <div className="flex justify-content-between align-items-center">
                        <div className="flex align-items-center gap-3">
                            <div className="flex align-items-center justify-content-center bg-green-100 border-round w-3rem h-3rem">
                                <i className="pi pi-wallet text-green-500 text-2xl"></i>
                            </div>
                            <div>
                                <h5 className="m-0 mb-1">Wallet Balance</h5>
                                <div className="text-3xl font-bold text-900">₹{walletBalance.toLocaleString()}</div>
                                <div className="text-500 mt-1">
                                    <span className="text-green-500 font-medium">
                                        {creditTransactions.summary?.net_change > 0 ? '+' : ''}
                                        ₹{creditTransactions.summary?.net_change || 0}
                                    </span>
                                    <span> this period</span>
                                </div>
                            </div>
                        </div>
                        <i className="pi pi-chevron-right text-500"></i>
                    </div>
                </Card>
            </div> */}

                {/* <div className="col-12 md:col-6">
                <Card 
                    className="mb-3 cursor-pointer transition-colors transition-duration-200 hover:surface-hover"
                    onClick={() => navigate('/app/report-history')}
                >
                    <div className="flex justify-content-between align-items-center">
                        <div className="flex align-items-center gap-3">
                            <div className="flex align-items-center justify-content-center bg-blue-100 border-round w-3rem h-3rem">
                                <i className="pi pi-file text-blue-500 text-2xl"></i>
                            </div>
                            <div>
                                <h5 className="m-0 mb-1">Total Reports</h5>
                                <div className="text-3xl font-bold text-900">{reportsSummary.period_total || 0}</div>
                                <div className="text-500 mt-1">
                                    <span className="text-green-500 font-medium">{reportsSummary.today || 0} today</span>
                                    <span> • {reportsSummary.daily_average || 0} avg/day</span>
                                </div>
                            </div>
                        </div>
                        <i className="pi pi-chevron-right text-500"></i>
                    </div>
                </Card>
            </div> */}

                {/* Professional Action Cards */}
                <style>{actionCardStyles}</style>

                <ActionCard
                    icon="pi-users"
                    color="blue"
                    title="Create Lending Network Profile"
                    description="Set up your agent profile to join the lending network and start earning commission"
                    cta="Get Started"
                    delayClass="animation-delay-100"
                    onClick={() => navigate('/app/my-agent-profile')}
                />

                <ActionCard
                    icon="pi-credit-card"
                    color="purple"
                    title="Create Lead and Check Loan Offer"
                    description="Compare personal loan offers from multiple banks with part payment and pre-closure details"
                    cta="Compare Offers"
                    delayClass="animation-delay-200"
                    onClick={() => navigate('/app/leads/create')}
                />

                <ActionCard
                    icon="pi-chart-bar"
                    color="green"
                    title="Check Borrower Financial Data"
                    description="Analyse bank statement or check borrower's experian report to verify eligibility"
                    cta="Check Now"
                    delayClass="animation-delay-300"
                    onClick={async () => {
                        const serviceId = await getEquifaxServiceId();
                        navigate(`/app/reports/equifax/${serviceId}`);
                    }}
                />

                <ActionCard
                    icon="pi-wallet"
                    color="orange"
                    title="Add Credits / Subscribe"
                    description="Recharge your credit wallet or upgrade your subscription plan for more features"
                    cta="Add Credits"
                    delayClass="animation-delay-400"
                    onClick={() => navigate('/app/credit-wallet')}
                />

                {/* Key Metrics Cards - Diamond Template Design */}
                <div className="col-12 lg:col-3 md:col-6">
                    <div className="surface-card p-4 border-round shadow-1 mt-5">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div className="text-500 font-medium">Wallet Balance</div>
                            <div className="border-circle w-2rem h-2rem bg-green-100 flex align-items-center justify-content-center">
                                <i className="pi pi-wallet text-green-600"></i>
                            </div>
                        </div>
                        <div className="text-900 text-xl font-medium mb-2">₹{walletBalance.toLocaleString()}</div>
                        <div className="text-green-500 text-sm">
                            <i className="pi pi-arrow-up text-xs mr-1"></i>
                            <span>₹{creditTransactions.summary?.net_change || 0} since last period</span>
                        </div>
                    </div>
                </div>

                <div className="col-12 lg:col-3 md:col-6">
                    <div className="surface-card p-4 border-round shadow-1 mt-5">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div className="text-500 font-medium">Total Reports</div>
                            <div className="border-circle w-2rem h-2rem bg-blue-100 flex align-items-center justify-content-center">
                                <i className="pi pi-file text-blue-600"></i>
                            </div>
                        </div>
                        <div className="text-900 text-xl font-medium mb-2">{reportsSummary.period_total || 0}</div>
                        <div className="text-blue-500 text-sm">
                            <span>{reportsSummary.today || 0} new since last visit</span>
                        </div>
                    </div>
                </div>

                <div className="col-12 lg:col-3 md:col-6">
                    <div className="surface-card p-4 border-round shadow-1 mt-5">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div className="text-500 font-medium">Success Rate</div>
                            <div className="border-circle w-2rem h-2rem bg-purple-100 flex align-items-center justify-content-center">
                                <i className="pi pi-chart-line text-purple-600"></i>
                            </div>
                        </div>
                        <div className="text-900 text-xl font-medium mb-2">
                            {reportsSummary.period_total > 0
                                ? Math.round((reportsSummary.by_status?.success / reportsSummary.period_total) * 100)
                                : 0}%
                        </div>
                        <div className="text-purple-500 text-sm">
                            <span>{reportsSummary.by_status?.success || 0} successful reports</span>
                        </div>
                    </div>
                </div>

                <div className="col-12 lg:col-3 md:col-6">
                    <div className="surface-card p-4 border-round shadow-1 mt-5">
                        <div className="flex align-items-center justify-content-between mb-3">
                            <div className="text-500 font-medium">Growth</div>
                            <div className="border-circle w-2rem h-2rem bg-orange-100 flex align-items-center justify-content-center">
                                <i className="pi pi-arrow-up text-orange-600"></i>
                            </div>
                        </div>
                        <div className="text-900 text-xl font-medium mb-2">+{periodComparison.change?.percentage || 0}%</div>
                        <div className="text-orange-500 text-sm">
                            <span>+{periodComparison.change?.absolute || 0} new since last period</span>
                        </div>
                    </div>
                </div>

                {/* Recent Reports Table */}
                {recentReports.length > 0 && (
                    <div className="col-12">
                        <Card title="Recent Reports">
                            <Toolbar
                                left={leftToolbarTemplate}
                                right={rightToolbarTemplate}
                                className="mb-3"
                            />

                            <DataTable
                                value={recentReports}
                                lazy
                                paginator
                                first={reportsFirst}
                                rows={reportsRows}
                                totalRecords={recentReports.length}
                                onPage={(e) => {
                                    setReportsFirst(e.first);
                                    setReportsRows(e.rows);
                                }}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} reports"
                                rowsPerPageOptions={[10, 20, 50]}
                                dataKey="report_id"
                                loading={refreshing}
                                emptyMessage="No reports found"
                                responsiveLayout="scroll"
                                scrollable
                                scrollHeight="400px"
                                stripedRows
                                size="small"
                                showGridlines
                                globalFilter={globalFilterValue}
                            >
                                <Column
                                    field="report_name"
                                    header="Report Name"
                                    body={reportNameTemplate}
                                    sortable
                                    className="min-w-12rem"
                                />
                                <Column
                                    field="report_id"
                                    header="Report ID"
                                    sortable
                                    className="min-w-8rem"
                                />
                                <Column
                                    field="lead_id"
                                    header="Lead ID"
                                    body={(rowData) => rowData.lead_id ? `#${rowData.lead_id}` : '-'}
                                    sortable
                                    className="min-w-8rem"
                                />
                                <Column
                                    field="lead_name"
                                    header="Lead Name"
                                    body={(rowData) => rowData.lead_name || '-'}
                                    sortable
                                    className="min-w-10rem"
                                />
                                <Column
                                    field="attachment_status"
                                    header="Status"
                                    body={attachmentStatusTemplate}
                                    sortable
                                    className="min-w-8rem"
                                />
                                <Column
                                    field="fetched_date"
                                    header="Date"
                                    body={fetchedDateTemplate}
                                    sortable
                                    className="min-w-10rem"
                                />
                                <Column
                                    header="Actions"
                                    body={actionTemplate}
                                    exportable={false}
                                    alignHeader="center"
                                    bodyClassName="text-center"
                                />
                            </DataTable>
                        </Card>
                    </div>
                )}

                {/* Credit Transactions Table */}
                {creditTransactions.transactions && creditTransactions.transactions.length > 0 && (
                    <div className="col-12">
                        <Card title="Recent Credit Transactions">
                            <DataTable
                                value={creditTransactions.transactions}
                                lazy
                                paginator
                                first={transactionsFirst}
                                rows={transactionsRows}
                                totalRecords={creditTransactions.transactions.length}
                                onPage={(e) => {
                                    setTransactionsFirst(e.first);
                                    setTransactionsRows(e.rows);
                                }}
                                paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                                currentPageReportTemplate="Showing {first} to {last} of {totalRecords} transactions"
                                rowsPerPageOptions={[5, 10, 20, 50]}
                                loading={refreshing}
                                emptyMessage="No transactions found"
                                className="p-datatable-sm"
                                responsiveLayout="scroll"
                            >
                                <Column field="created_at" header="Date" body={transactionDateTemplate} sortable className="w-12rem" />
                                <Column field="type" header="Type" body={transactionTypeTemplate} sortable className="w-8rem" />
                                <Column field="amount" header="Credits" sortable className="w-8rem"
                                    body={(rowData) => {
                                        const isCredit = rowData.type === 'credit' || rowData.type === 1;
                                        return (
                                            <span className={isCredit ? 'text-green-500 font-semibold' : 'text-red-500 font-semibold'}>
                                                {isCredit ? '+' : '-'}₹{(rowData.amount || rowData.credits || 0).toLocaleString()}
                                            </span>
                                        );
                                    }}
z                                />
                                <Column field="reason" header="Description" />
                                <Column field="reference" header="Reference" className="w-12rem"
                                    body={(rowData) => rowData.reference || '-'}
                                />
                                <Column field="note" header="Notes" className="w-12rem"
                                    body={(rowData) => rowData.note || '-'}
                                />
                            </DataTable>
                        </Card>
                    </div>
                )}

                {/* Charts Section */}
                {data.report_types_distribution && data.report_types_distribution.length > 0 && (
                    <div className="col-12 lg:col-6">
                        <Card title="Report Types Distribution" className="h-full">
                            <Chart type="doughnut" data={reportTypesChart} options={reportTypesChartOptions} className="h-21rem" />
                        </Card>
                    </div>
                )}

                {creditTransactions.summary && (
                    <div className="col-12 lg:col-6">
                        <Card title="Credit Usage Overview" className="h-full">
                            <Chart type="bar" data={creditTrendsChart} options={creditTrendsChartOptions} className="h-21rem" />
                        </Card>
                    </div>
                )}

                {/* Report Status Summary */}
                {/* <div className="col-12 md:col-4">
                <Card title="Report Status Summary">
                    <div className="flex flex-column gap-3">
                        <div className="flex justify-content-between align-items-center">
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-check-circle text-green-500"></i>
                                <span>Successful</span>
                            </span>
                            <span className="font-semibold">{reportsSummary.by_status?.success || 0}</span>
                        </div>
                        <div className="flex justify-content-between align-items-center">
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-exclamation-circle text-yellow-500"></i>
                                <span>Pending</span>
                            </span>
                            <span className="font-semibold">{reportsSummary.by_status?.pending || 0}</span>
                        </div>
                        <div className="flex justify-content-between align-items-center">
                            <span className="flex align-items-center gap-2">
                                <i className="pi pi-times-circle text-red-500"></i>
                                <span>Failed</span>
                            </span>
                            <span className="font-semibold">{reportsSummary.by_status?.failed || 0}</span>
                        </div>
                    </div>
                </Card>
            </div> */}

                {/* Period Comparison Card */}
                {/* <div className="col-12 md:col-4">
                <Card title="Period Comparison">
                    <div className="flex flex-column gap-3">
                        <div>
                            <span className="text-500 block mb-2">Current Period</span>
                            <div className="flex justify-content-between align-items-center">
                                <span className="font-semibold text-xl">{periodComparison.current_period?.reports || 0} Reports</span>
                                <Tag 
                                    value={periodComparison.change?.trend || 'stable'} 
                                    severity={periodComparison.change?.trend === 'up' ? 'success' : 
                                             periodComparison.change?.trend === 'down' ? 'danger' : 'info'}
                                    icon={`pi pi-arrow-${periodComparison.change?.trend === 'up' ? 'up' : 
                                          periodComparison.change?.trend === 'down' ? 'down' : 'right'}`}
                                />
                            </div>
                        </div>
                        <div className="border-top-1 surface-border pt-3">
                            <span className="text-500 block mb-2">Previous Period</span>
                            <span className="font-semibold">{periodComparison.previous_period?.reports || 0} Reports</span>
                        </div>
                    </div>
                </Card>
            </div> */}

                {/* Quick Actions Card */}
                {/* <div className="col-12 md:col-4">
                <Card title="Quick Actions">
                    <div className="flex flex-column gap-2">
                        <Button 
                            label="Generate New Report" 
                            icon="pi pi-plus" 
                            className="p-button-outlined w-full"
                            onClick={() => navigate('/app/equifax-report')}
                        />
                        <Button 
                            label="View All Reports" 
                            icon="pi pi-list" 
                            className="p-button-outlined w-full"
                            onClick={() => navigate('/app/report-history')}
                        />
                        <Button 
                            label="Add Credits" 
                            icon="pi pi-wallet" 
                            className="p-button-success w-full"
                            onClick={() => navigate('/app/wallet')}
                        />
                    </div>
                </Card>
            </div> */}
            </div>
        </>
    );
};

export default UnifiedDashboard;